import { DOCUMENT } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminUserApiService } from '@cnt-admin/data-access';
import { IUser } from '@curbnturf/entities';
import { IonButton, IonItem, IonLabel, IonList, IonSearchbar } from '@ionic/angular/standalone';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { restoreFocus } from '../a11y/focus';

/**
 * Server-side user search (GET /user/paginated) for picking a listing/boondock
 * owner. Replaces loading every user through @curbnturf/user's UserFacade.
 */
@Component({
  selector: 'admin-user-picker',
  standalone: true,
  imports: [IonButton, IonItem, IonLabel, IonList, IonSearchbar],
  template: `
    @if (selected) {
      <ion-item lines="none">
        <ion-label>
          @if (label) {
            <span class="admin-visually-hidden">{{ label }}: </span>
          }
          {{ userText(selected) }}
        </ion-label>
        <ion-button
          slot="end"
          fill="clear"
          size="small"
          [attr.aria-label]="label ? 'Change ' + label.toLowerCase() : 'Change user'"
          (click)="clear($event)"
          >Change</ion-button
        >
      </ion-item>
    } @else {
      <ion-searchbar
        [placeholder]="placeholder"
        [debounce]="0"
        (ionInput)="terms$.next($any($event).detail.value ?? '')"
      ></ion-searchbar>
      @if (results.length) {
        <ion-list class="results">
          @for (user of results; track user.id) {
            <ion-item button detail="false" (click)="pick(user, $event)">
              <ion-label>
                {{ userText(user) }}
                <p>{{ user.email }}</p>
              </ion-label>
            </ion-item>
          }
        </ion-list>
      }
    }
  `,
  styles: [
    `
      .results {
        max-height: 280px;
        overflow-y: auto;
        margin-top: 6px;
        padding: 4px;
        border: 1px solid var(--cnt-line);
        border-radius: 0.75rem;
        background: #fff;
      }
      .results ion-item {
        --border-radius: 0.5rem;
        --background-hover: var(--cnt-cream);
        --background-hover-opacity: 1;
      }
    `,
  ],
})
export class UserPickerComponent implements OnInit {
  private readonly users = inject(AdminUserApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** What's being picked, e.g. "Guest": names the search box and its Change button for screen readers. */
  @Input() label = '';
  @Input() selected?: IUser;
  @Input() placeholder = 'Search users by name, email or id';
  @Output() readonly selectedChange = new EventEmitter<IUser | undefined>();

  /**
   * Names the search box's input. Ionic's searchbar labels its input "search text" and
   * passes no aria-label down, so it's set on the input itself (each time the box is shown).
   */
  @ViewChild(IonSearchbar)
  set searchbar(searchbar: IonSearchbar | undefined) {
    if (!searchbar || !this.label) return;
    void searchbar.getInputElement().then((input) => input.setAttribute('aria-label', this.label));
  }

  readonly terms$ = new Subject<string>();
  results: IUser[] = [];

  ngOnInit(): void {
    this.terms$
      .pipe(
        debounceTime(300),
        map((t) => t.trim()),
        distinctUntilChanged(),
        switchMap((term) =>
          term.length < 2
            ? of<IUser[]>([])
            : this.users.search(term, 1, 20).pipe(
                map((page) => page.users),
                catchError(() => of<IUser[]>([])),
              ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((users) => (this.results = users));
  }

  /** Picks a user; focus moves from the result (now gone) to the Change button. */
  pick(user: IUser, event?: Event): void {
    const trigger = (event?.currentTarget as Element | null) ?? null;
    this.selected = user;
    this.results = [];
    this.selectedChange.emit(user);
    void restoreFocus(this.document, trigger, () => [this.host.nativeElement.querySelector('ion-button')]);
  }

  /** Back to searching; focus moves from Change (now gone) to the search box. */
  clear(event?: Event): void {
    const trigger = (event?.currentTarget as Element | null) ?? null;
    this.selected = undefined;
    this.selectedChange.emit(undefined);
    void restoreFocus(this.document, trigger, () => [this.host.nativeElement.querySelector('ion-searchbar')]);
  }

  userText(user: IUser): string {
    return `${user.firstName ?? ''} ${user.lastName ?? ''} (${user.id})`.trim();
  }
}
